/* My Votes journal. The installed voting app owns authentication and release rules. */
(() => {
  'use strict';
  const VIEWS = ['history', 'upcoming', 'released'];
  const ENDPOINTS = { history: '/my-votes', upcoming: '/upcoming-winners', released: '/released-products' };
  const PAGE_SIZE = 20;
  const el = (tag, className, value) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (value !== undefined) node.textContent = String(value);
    return node;
  };
  const button = (label, className, action) => {
    const node = el('button', className, label);
    node.type = 'button';
    if (action) node.addEventListener('click', action);
    return node;
  };
  const icon = (path) => {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('stroke-width', '1.5');
    svg.setAttribute('stroke-linecap', 'round');
    svg.setAttribute('stroke-linejoin', 'round');
    svg.setAttribute('aria-hidden', 'true');
    const line = document.createElementNS(svg.namespaceURI, 'path');
    line.setAttribute('d', path);
    svg.append(line);
    return svg;
  };
  const validDate = value => {
    if (!value) return null;
    const date = new Date(value);
    return Number.isFinite(date.getTime()) ? date : null;
  };
  // History and event headers use the round's start date, never its internal app name.
  const roundTitle = (value, format, fallback) => {
    const date = validDate(value);
    if (!date) return fallback;
    let locale = document.documentElement.lang || 'en';
    try { new Intl.DateTimeFormat(locale); } catch (_) { locale = 'en'; }
    const label = new Intl.DateTimeFormat(locale, { month:'long', day:'numeric', year:'numeric' }).format(date);
    return format.split('__DATE__').join(label);
  };

  function mount(root) {
    if (root.dataset.mvMounted) return;
    root.dataset.mvMounted = 'true';
    const eventTitle = root.querySelector('[data-mv-event-title]');
    if (eventTitle) {
      eventTitle.textContent = roundTitle(eventTitle.dataset.mvStartTime, eventTitle.dataset.mvTitleFormat, eventTitle.dataset.mvTitleFallback);
      document.title = eventTitle.textContent + (eventTitle.dataset.mvShopName ? ' | ' + eventTitle.dataset.mvShopName : '');
    }
    const previewLocation = () => {
      const current = new URL(location.href), theme = window.Shopify?.theme;
      if (!current.searchParams.has('preview_theme_id') && ['unpublished', 'development'].includes(String(theme?.role).toLowerCase()) && /^\d+$/.test(String(theme?.id))) current.searchParams.set('preview_theme_id', String(theme.id));
      return current;
    };
    const currentUrl = previewLocation();
    root.querySelectorAll('[data-mv-preserve-preview]').forEach(anchor => {
      const url = new URL(anchor.href);
      ['preview_theme_id', '_ab', '_fd', '_sc'].forEach(key => {
        if (currentUrl.searchParams.has(key)) url.searchParams.set(key, currentUrl.searchParams.get(key));
      });
      anchor.href = url.href;
    });
    // Linked voting events use the shared header; the existing app alone initializes their voting content.
    if (root.dataset.mvView === 'survey' || root.dataset.authenticated !== 'true') return;
    let copy;
    try { copy = JSON.parse(root.querySelector('[data-mv-copy]').textContent); } catch (_) { return; }
    const t = (key, values = {}) => Object.entries(values).reduce(
      (text, [name, value]) => text.split('__' + name.toUpperCase() + '__').join(String(value)), copy[key] || key);

    let locale = document.documentElement.lang || 'en';
    try { new Intl.DateTimeFormat(locale); } catch (_) { locale = 'en'; }
    const dateText = value => {
      const date = validDate(value);
      return date ? new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric', year: 'numeric' }).format(date) : t('date_unavailable');
    };
    const route = (kind, handle, preview = true) => {
      if (typeof handle !== 'string' || !handle.trim() || /[\s/\\?#]|^\.{1,2}$/.test(handle)) return null;
      const url = new URL((root.dataset.rootUrl || '/') + kind + '/' + encodeURIComponent(handle), location.origin);
      if (preview) {
        const current = previewLocation();
        ['preview_theme_id', '_ab', '_fd', '_sc'].forEach(key => {
          if (current.searchParams.has(key)) url.searchParams.set(key, current.searchParams.get(key));
        });
      }
      return url;
    };
    const link = (label, url, className = 'dac-mv-button') => {
      const node = el('a', className, label); node.href = String(url); return node;
    };
    const image = (holder, source, alt = '') => {
      holder.replaceChildren();
      let url;
      try { url = new URL(source, location.origin); } catch (_) { /* Missing artwork stays masked. */ }
      if (!source || !url || (url.protocol !== 'https:' && url.origin !== location.origin)) {
        holder.append(el('span', 'dac-mv-image-fallback', t('image_unavailable'))); return;
      }
      const img = el('img'); img.src = url.href; img.alt = alt; img.loading = 'lazy'; img.decoding = 'async';
      img.addEventListener('error', () => holder.replaceChildren(el('span', 'dac-mv-image-fallback', t('image_unavailable'))), { once: true });
      holder.append(img);
    };

    // Listen for late authentication, and recognize an already initialized app without reading its token.
    const appReady = signal => new Promise((resolve, reject) => {
      let timer;
      const cleanup = () => {
        clearTimeout(timer);
        window.removeEventListener('vp-app-customer-auth', check);
        window.removeEventListener('vp-app-loaded', check);
        signal.removeEventListener('abort', aborted);
      };
      const aborted = () => { cleanup(); reject(new DOMException('Aborted', 'AbortError')); };
      const check = () => {
        if (window.VP && typeof window.VP.fetch === 'function' && Boolean(window.VP.AUTH_TOKEN)) {
          cleanup(); resolve(window.VP);
        }
      };
      timer = setTimeout(() => { cleanup(); reject(new Error('Voting app unavailable')); }, 20000);
      window.addEventListener('vp-app-customer-auth', check);
      window.addEventListener('vp-app-loaded', check);
      signal.addEventListener('abort', aborted, { once: true });
      if (signal.aborted) aborted(); else check();
    });
    const request = async (path, controller, init = {}) => {
      const app = await appReady(controller.signal);
      const attempt = new AbortController();
      let timeout, abort;
      const deadline = new Promise((_, reject) => {
        abort = () => { reject(new DOMException('Aborted', 'AbortError')); attempt.abort(); };
        controller.signal.addEventListener('abort', abort, { once: true });
        timeout = setTimeout(() => { reject(new Error('Request timed out')); attempt.abort(); }, 25000);
      });
      try {
        if (controller.signal.aborted) { abort(); return await deadline; }
        return await Promise.race([app.fetch(path, { ...init, signal: attempt.signal }), deadline]);
      } finally { clearTimeout(timeout); controller.signal.removeEventListener('abort', abort); }
    };

    const storageKey = 'dac-my-votes-view:' + location.pathname;
    const pageNumber = value => Number.isInteger(value) && value > 0 && value < 100000 ? value : 1;
    let restored = null;
    try {
      const saved = JSON.parse(sessionStorage.getItem(storageKey));
      if (saved && Date.now() - saved.savedAt < 1800000) restored = saved;
    } catch (_) { /* Browsing remains available without session storage. */ }
    let active = restored && VIEWS.includes(restored.active) ? restored.active : 'history';
    const navigation = performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
    let restoreScroll = navigation && navigation.type === 'back_forward' && restored && Number.isFinite(restored.scrollY) ? restored.scrollY : null;
    const states = Object.fromEntries(VIEWS.map(view => [view, {
      page: pageNumber(restored && restored.pages && restored.pages[view]),
      requested: 1, items: null, hasNext: false, busy: false, serial: 0, controller: null
    }]));
    const saveState = () => {
      try { sessionStorage.setItem(storageKey, JSON.stringify({ active, pages: Object.fromEntries(VIEWS.map(view => [view, states[view].page])), scrollY: window.scrollY, savedAt: Date.now() })); }
      catch (_) { /* No customer records or authentication data are stored here. */ }
    };
    window.addEventListener('pagehide', saveState);
    const tabs = Array.from(root.querySelectorAll('[data-mv-tab]'));
    const ui = {};
    VIEWS.forEach(view => {
      const panel = root.querySelector('[data-mv-panel="' + view + '"]');
      const heading = el('div', 'dac-mv-panel-heading');
      const titleGroup = el('h2', 'dac-mv-sr-only', t(view + '_title'));
      const count = el('span', 'dac-mv-result-count');
      heading.append(titleGroup, count);
      const status = el('p', 'dac-mv-sr-only'); status.setAttribute('role', 'status'); status.setAttribute('aria-atomic', 'true');
      const error = el('div', 'dac-mv-error'); error.hidden = true;
      const items = el('div', view === 'history' ? 'dac-mv-items' : 'dac-mv-art-grid');
      const nav = el('nav', 'dac-mv-pagination'); nav.setAttribute('aria-label', t(view) + ' — ' + t('pagination'));
      const previous = button(t('previous'), 'dac-mv-button', () => load(view, states[view].page - 1, true));
      const page = el('span'); page.setAttribute('aria-current', 'page');
      const next = button(t('next'), 'dac-mv-button', () => load(view, states[view].page + 1, true));
      nav.append(previous, page, next); nav.hidden = true;
      panel.replaceChildren(heading, status, error, items, nav);
      ui[view] = { panel, count, status, error, items, nav, previous, page, next };
    });

    function pagination(view) {
      const s = states[view], u = ui[view];
      u.previous.disabled = s.busy || s.page <= 1;
      u.next.disabled = s.busy || !s.hasNext;
      u.page.textContent = t('page', { page: s.page });
      u.nav.hidden = !s.items || (s.page === 1 && !s.hasNext);
    }
    function errorView(view, error) {
      const u = ui[view];
      u.error.replaceChildren(el('p', '', t(error && error.status === 401 ? 'session_error' : 'load_error')));
      const actions = el('div', 'dac-mv-error-actions');
      actions.append(button(t('retry'), 'dac-mv-button', () => load(view, states[view].requested)));
      if (error && error.status === 401) actions.append(link(t('sign_in_again'), root.dataset.signIn));
      u.error.append(actions); u.error.hidden = false;
      u.status.textContent = t(error && error.status === 401 ? 'session_error' : 'load_error');
    }
    function countText(view, count) {
      const nouns = { history: ['survey', 'surveys'], upcoming: ['artwork', 'artworks'], released: ['kit', 'kits'] }[view];
      return t(count === 1 ? 'showing_one_' + nouns[0] : 'showing_' + nouns[1], { count });
    }
    async function load(view, target = 1, userPaged = false) {
      const s = states[view], u = ui[view];
      if (s.controller) s.controller.abort();
      const serial = ++s.serial, controller = new AbortController();
      s.controller = controller; s.requested = pageNumber(target); s.busy = true;
      u.error.hidden = true; u.status.textContent = t('loading'); u.items.setAttribute('aria-busy', 'true');
      if (s.items === null) {
        u.items.replaceChildren();
        for (let index = 0; index < (view === 'history' ? 3 : 4); index++) {
          const skeleton = el('div', 'dac-mv-skeleton'); skeleton.setAttribute('aria-hidden', 'true'); u.items.append(skeleton);
        }
      }
      pagination(view);
      try {
        const response = await request(ENDPOINTS[view] + '?page=' + s.requested, controller);
        if (s.serial !== serial) return;
        if (!response || !Array.isArray(response.data) || response.data.some(item => !item || typeof item !== 'object')) throw new Error('Invalid voting response');
        // The history API's totals include hidden surveys. Never label that count as votes or offer empty trailing pages.
        if (!response.data.length && s.requested > 1) {
          if (!s.items || !s.items.length) { s.busy = false; await load(view, 1); return; }
          s.hasNext = false; u.status.textContent = t('end_reached');
        } else {
          s.page = s.requested; s.items = response.data;
          s.hasNext = response.hasNextPage === true && response.data.length >= PAGE_SIZE;
          render(view); u.status.textContent = countText(view, s.items.length) + '. ' + t('page', { page: s.page });
          if (restoreScroll !== null && active === view) {
            const position = restoreScroll; restoreScroll = null;
            requestAnimationFrame(() => window.scrollTo({ top: position, behavior: 'instant' }));
          }
          if (userPaged && active === view) {
            const heading = u.panel; heading.tabIndex = -1;
            heading.focus({ preventScroll: true });
            heading.scrollIntoView({ block: 'start', behavior: 'instant' });
          }
        }
        saveState();
      } catch (error) {
        if (s.serial !== serial) return;
        if (s.items === null) u.items.replaceChildren();
        errorView(view, error);
      } finally {
        if (s.serial === serial) {
          s.busy = false; u.items.removeAttribute('aria-busy'); pagination(view);
        }
      }
    }
    function switchView(view) {
      active = view;
      tabs.forEach(tab => {
        const selected = tab.dataset.mvTab === view;
        tab.setAttribute('aria-selected', String(selected)); tab.tabIndex = selected ? 0 : -1;
      });
      VIEWS.forEach(name => { ui[name].panel.hidden = name !== view; });
      saveState();
      if (states[view].items === null && !states[view].busy) load(view, states[view].page);
    }
    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => switchView(tab.dataset.mvTab));
      tab.addEventListener('keydown', event => {
        let next;
        if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
        if (event.key === 'ArrowLeft') next = (index + tabs.length - 1) % tabs.length;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = tabs.length - 1;
        if (next !== undefined) { event.preventDefault(); tabs.forEach((item, i) => { item.tabIndex = i === next ? 0 : -1; }); tabs[next].focus(); }
      });
    });
    function render(view) {
      const s = states[view], u = ui[view];
      u.count.textContent = s.items.length ? countText(view, s.items.length) : '';
      u.panel.classList.toggle('dac-mv-is-empty', !s.items.length);
      u.items.replaceChildren();
      u.items.className = view === 'history' ? 'dac-mv-items' : 'dac-mv-art-grid';
      if (!s.items.length) {
        u.items.className = '';
        const empty = el('div', 'dac-mv-empty dac-mv-library-empty');
        empty.dataset.mvEmpty = view;
        const message = el('div', 'dac-mv-empty-copy');
        message.append(el('h3', '', t(view + '_empty_title')), el('p', '', t(view + '_empty_copy')));
        if (view !== 'history') message.append(button(t('view_history'), 'dac-mv-text-button', () => {
          switchView('history'); tabs.find(tab => tab.dataset.mvTab === 'history').focus();
        }));
        empty.append(icon(view === 'released' ? 'm3 7 9-4 9 4-9 4zM3 7v10l9 4 9-4V7M12 11v10' : 'M5 4h14v17H5zM8 2v4M16 2v4M5 9h14M9 14l2 2 4-4'), message);
        u.items.append(empty); return;
      }
      s.items.forEach((item, index) => u.items.append(view === 'history' ? historyCard(item) : artworkCard(item, view, index)));
    }
    function historyCard(item) {
      const page = item.page || {}, card = el('article', 'dac-mv-history-card'), name = el('div', 'dac-mv-history-name');
      name.append(el('h3', '', roundTitle(page.startTime, t('round_title'), t('survey'))));
      if (Number.isInteger(item.totalVotes) && item.totalVotes >= 0) name.append(el('span', 'dac-mv-picks', item.totalVotes === 1 ? t('one_pick') : t('many_picks', { count: item.totalVotes })));
      // A round with no picks has nothing to share, so it keeps View votes only.
      const hasPicks = Number.isInteger(item.totalVotes) ? item.totalVotes > 0 : true;
      if (!hasPicks) card.classList.add('dac-mv-history-card--no-picks');
      const actions = el('div', 'dac-mv-history-actions'), url = route('pages', page.handle), ended = validDate(page.endTime);
      if (url) actions.append(link(t('view_votes'), url));
      else actions.append(el('p', 'dac-mv-share-note', t('survey_unavailable')));
      if (url && hasPicks && item.pageId && ended && ended.getTime() < Date.now()) {
        actions.append(button(t('share_votes'), 'dac-mv-text-button', event => share(item, event.currentTarget)));
      } else if (url && hasPicks) actions.append(el('p', 'dac-mv-share-note', t('share_after')));
      card.append(name, actions); return card;
    }
    function releaseDate(item, released) {
      const node = el('p', 'dac-mv-release-date');
      node.append(el('span', '', t(released ? 'released_date' : 'release_date')), el('strong', '', validDate(item.productReleaseDate) ? dateText(item.productReleaseDate) : t('release_unannounced')));
      return node;
    }
    function artworkCard(item, view, index) {
      const card = el('article', 'dac-mv-art-card'), released = view === 'released';
      const artwork = button('', 'dac-mv-artwork', released ? null : event => preview(index, event.currentTarget));
      artwork.setAttribute('aria-label', released ? t('view_kit') + ' — ' + (item.title || t('view_kit')) : t('preview') + ' — ' + dateText(item.productReleaseDate));
      image(artwork, item.shopifyPreviewUrl, released ? item.title || '' : '');
      const details = el('div', 'dac-mv-art-copy');
      if (!released) {
        const hint = el('span', 'dac-mv-preview-hint'); hint.setAttribute('aria-hidden', 'true'); hint.append(icon('M10 3a7 7 0 1 0 0 14 7 7 0 0 0 0-14M15 15l6 6M7 10h6M10 7v6')); artwork.append(hint);
        const artist = typeof item.vendor === 'string' ? item.vendor.trim() : '';
        if (artist) details.append(el('p', 'dac-mv-credit', artist));
        details.append(releaseDate(item, false));
      } else {
        const status = el('p', 'dac-mv-card-error'); status.setAttribute('role', 'status'); status.hidden = true;
        let opening = false;
        const open = button(item.title || t('view_kit'), 'dac-mv-kit-link', async () => {
          if (opening) return;
          opening = true; open.disabled = true; artwork.disabled = true;
          status.textContent = t('opening_kit'); status.hidden = false; status.classList.add('dac-mv-card-pending');
          try {
            const result = await request('/product-handle?productId=' + encodeURIComponent(item.productId), new AbortController());
            const url = route('products', result && result.handle);
            if (!url) throw new Error('Kit unavailable');
            saveState(); status.hidden = true; location.assign(url.href);
          } catch (_) { status.textContent = t('kit_error'); status.hidden = false; }
          finally { opening = false; open.disabled = false; artwork.disabled = false; status.classList.remove('dac-mv-card-pending'); }
        });
        open.setAttribute('aria-label', t('view_kit') + ' — ' + (item.title || t('view_kit')));
        artwork.tabIndex = -1; artwork.addEventListener('click', () => open.click());
        if (!item.productId) { open.disabled = true; artwork.disabled = true; status.hidden = false; status.textContent = t('kit_unavailable'); }
        const name = el('h3'); name.append(open); details.append(name);
        if (item.vendor) details.append(el('p', 'dac-mv-credit', item.vendor));
        details.append(releaseDate(item, true), status);
      }
      card.append(artwork, details); return card;
    }

    let dialog = null, dialogSequence = 0;
    function openDialog(title, trigger, className = '') {
      if (dialog) dialog.close();
      const sequence = ++dialogSequence;
      const node = el('dialog', 'dac-mv-dialog ' + className), header = el('div', 'dac-mv-dialog-head');
      const titleNode = el('h2', '', title); titleNode.id = root.id + '-dialog-' + sequence; titleNode.tabIndex = -1;
      node.setAttribute('aria-labelledby', titleNode.id);
      const close = button('', 'dac-mv-close', () => node.close()); close.setAttribute('aria-label', t('close')); close.append(icon('m6 6 12 12M18 6 6 18'));
      if (window.DACCloseControl) window.DACCloseControl.apply(close, t('close'));
      header.append(titleNode, close); node.append(header); document.body.append(node);
      const overflow = document.body.style.overflow;
      const controller = new AbortController();
      node.addEventListener('close', () => {
        controller.abort(); document.body.style.overflow = overflow; node.remove();
        if (dialog === node) dialog = null;
        if (trigger && trigger.isConnected) trigger.focus({ preventScroll: true });
      }, { once: true });
      node.addEventListener('click', event => {
        if (event.target !== node) return;
        const rect = node.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) node.close();
      });
      document.body.style.overflow = 'hidden'; dialog = node; node.showModal(); titleNode.focus({ preventScroll: true });
      return { node, controller };
    }
    function share(item, trigger) {
      const { node, controller } = openDialog(t('share_title'), trigger);
      const content = el('div', 'dac-mv-dialog-content');
      content.append(el('p', 'dac-mv-dialog-copy', t('share_explanation')));
      const status = el('p', 'dac-mv-share-status'); status.setAttribute('role', 'status'); status.setAttribute('aria-atomic', 'true');
      const result = el('div'); content.append(status, result); node.append(content);
      const create = async () => {
        result.replaceChildren(); status.textContent = t('share_pending');
        try {
          const response = await request('/my-votes/' + encodeURIComponent(item.pageId) + '/share', controller, { method: 'POST' });
          if (!node.open || controller.signal.aborted) return;
          const key = response && response.data && response.data.shareKey, url = route('pages', item.page.handle, false);
          if (typeof key !== 'string' || !key || !url) throw new Error('Share unavailable');
          url.searchParams.set('share_key', key); status.textContent = t('share_ready');
          const field = el('label', 'dac-mv-share-field', t('share_label')), input = el('input');
          input.type = 'text'; input.readOnly = true; input.value = url.href; input.addEventListener('click', () => input.select()); field.append(input);
          const copyButton = button(t('copy_link'), 'dac-mv-button dac-mv-primary dac-mv-copy', async () => {
            try {
              if (!navigator.clipboard || !navigator.clipboard.writeText) throw new Error('Clipboard unavailable');
              await navigator.clipboard.writeText(url.href);
              if (!node.open) return;
              copyButton.textContent = t('copied'); status.textContent = t('copy_success');
            } catch (_) {
              if (!node.open) return;
              status.textContent = t('copy_manual'); input.focus(); input.select();
            }
          });
          result.append(field, copyButton);
        } catch (error) {
          if (!node.open || controller.signal.aborted) return;
          status.textContent = t(error && error.status === 401 ? 'session_error' : 'share_error');
          if (error && error.status === 401) result.append(link(t('sign_in_again'), root.dataset.signIn));
          else result.append(button(t('retry'), 'dac-mv-button', create));
        }
      };
      create();
    }
    function preview(initialIndex, trigger) {
      const artworks = states.upcoming.items.slice();
      let index = initialIndex;
      const { node, controller } = openDialog(t('preview_title'), trigger, 'dac-mv-art-dialog');
      const figure = el('figure', 'dac-mv-preview-figure'), footer = el('div', 'dac-mv-preview-footer');
      const previous = button('', 'dac-mv-button dac-mv-arrow', () => move(-1)), next = button('', 'dac-mv-button dac-mv-arrow', () => move(1));
      previous.setAttribute('aria-label', t('previous_art')); next.setAttribute('aria-label', t('next_art'));
      previous.append(icon('m15 5-7 7 7 7')); next.append(icon('m9 5 7 7-7 7'));
      const caption = el('p'); caption.setAttribute('role', 'status'); caption.setAttribute('aria-atomic', 'true');
      footer.append(previous, caption, next); node.append(figure, footer);
      if (window.DACVotesImageGestures) window.DACVotesImageGestures.bind({ surface: figure, dialog: node, isOpen: () => node.open, close: () => node.close(), signal: controller.signal });
      const update = () => {
        const item = artworks[index];
        image(figure, item.shopifyPreviewUrl, t('preview_title'));
        caption.replaceChildren(el('strong', '', t('preview_position', { count: index + 1, total: artworks.length })), document.createTextNode(t('release_date') + ' ' + dateText(item.productReleaseDate)));
        const artist = typeof item.vendor === 'string' ? item.vendor.trim() : '';
        if (artist) caption.insertBefore(el('span', 'dac-mv-credit', artist), caption.lastChild).after(el('br'));
        previous.disabled = index === 0; next.disabled = index === artworks.length - 1;
      };
      function move(amount) { const nextIndex = index + amount; if (nextIndex >= 0 && nextIndex < artworks.length) { index = nextIndex; update(); } }
      node.addEventListener('keydown', event => {
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') { event.preventDefault(); move(event.key === 'ArrowRight' ? 1 : -1); }
      });
      update();
    }
    switchView(active);
  }
  const start = () => document.querySelectorAll('[data-mv-root]').forEach(mount);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true }); else start();
  document.addEventListener('shopify:section:load', start);
})();
